import { useEffect, useState } from 'react';

import { Button, ConditionBuilder, Drawer, type ConditionField } from '@/components/ui';
import type { ConditionTree } from '@/api/queries/workspace';
import { useConditionValueSuggestions } from '@/api/queries/conditionFields';

import { conditionsToQuery, parseQuery } from './parseQuery';

const EMPTY_TREE: ConditionTree = { conjunction: 'and', conditions: [] };

interface ContactsFilterDrawerProps {
  open: boolean;
  onClose: () => void;
  /** The currently applied filter. Seeds the staged draft each time the drawer opens. */
  value: ConditionTree;
  fields: ConditionField[];
  /** Called with the staged tree when the user clicks Apply. */
  onApply: (next: ConditionTree) => void;
}

/**
 * The single advanced-filter surface for the contacts list. Edits are staged in
 * a local draft (same controlled-draft pattern the old inline filters used) and
 * only hit the list on Apply, so the grid doesn't thrash while you build a query.
 */
export function ContactsFilterDrawer({
  open,
  onClose,
  value,
  fields,
  onApply,
}: ContactsFilterDrawerProps) {
  const [draft, setDraft] = useState<ConditionTree>(value);
  const [raw, setRaw] = useState('');

  // Re-seed the draft from the applied value whenever the drawer (re)opens.
  useEffect(() => {
    if (open) {
      setDraft(value);
      setRaw(conditionsToQuery(value, fields));
    }
  }, [open, value, fields]);

  // Keep the raw box in sync when the builder edits the tree.
  useEffect(() => {
    setRaw(conditionsToQuery(draft, fields));
  }, [draft, fields]);

  const count = draft.conditions?.length ?? 0;
  const applyRaw = () => setDraft(parseQuery(raw, fields));

  const apply = () => {
    onApply(draft);
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Filters"
      subtitle={count > 0 ? `${count} condition${count === 1 ? '' : 's'}` : 'No conditions'}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={() => setDraft(EMPTY_TREE)} disabled={count === 0}>
            Clear all
          </Button>
          <span style={{ flex: 1 }} />
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={apply}>
            Apply
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <ConditionBuilder
          value={draft}
          onChange={setDraft}
          fields={fields}
          useValueSuggestions={useConditionValueSuggestions}
        />

        <details className="filter-advanced">
          <summary style={{ fontSize: 12, color: 'var(--text-2)', cursor: 'pointer', userSelect: 'none' }}>
            Advanced
          </summary>
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 4 }}>
              Power query — e.g. <code>email:john status.active=true</code> or{' '}
              <code>lastName:^No OR tags:(vip|customer)</code>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                className="ds-input"
                style={{ flex: 1, height: 30, fontSize: 12.5, fontFamily: 'var(--font-mono)' }}
                value={raw}
                placeholder="field:value  field=value  field>n  field:*"
                onChange={(e) => setRaw(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyRaw();
                  }
                }}
                aria-label="Power query"
              />
              <Button variant="secondary" size="sm" onClick={applyRaw}>
                Parse
              </Button>
            </div>
          </div>
        </details>
      </div>
    </Drawer>
  );
}
